import '@/icons/bwi/all-bwi.css'
import '@/css/app.css'

import { createInertiaApp, router } from '@inertiajs/react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { datadogRum } from '@datadog/browser-rum'
import { reactPlugin } from '@datadog/browser-rum-react'

import ErrorBoundary from '@/components/error-boundary.js'
import BaseLayout from '@/layouts/base-layout'

declare global {
  interface Window {
    _hsq: any[]
    HubSpotConversations?: {
      widget: {
        refresh: () => void
      }
    }
  }
}

// Initialize HubSpot queue early to prevent GTM errors
if (typeof window !== 'undefined') {
  window._hsq = window._hsq || []
}

createInertiaApp({
  resolve: async (name: string) => {
    const pages = import.meta.glob<true, any>('./pages/**/*.tsx')
    const page = await pages[`./pages/${name}.tsx`]()
    page.default.layout = page.default.layout || ((page: any) => <BaseLayout page={page} />)
    return page
  },
  setup({ el, App, props }: { el: HTMLElement; App: any; props: any }) {
    // The server sends a canonical URL without the query string so the cached
    // document is identical for every visitor. Restore the real URL before
    // Inertia initialises, or it pushes history to the canonical one and drops
    // campaign parameters from both the address bar and the setPath call below.
    // Inertia applies the hash itself via page.setUrlHash().
    if (typeof window !== 'undefined') {
      props.initialPage.url = window.location.pathname + window.location.search
    }

    if (import.meta.env.VITE_DATADOG_CLIENT_TOKEN) {
      datadogRum.init({
        applicationId: '5fc33945-8ff7-4600-9a42-a275eefc22d8',
        clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
        site: 'us3.datadoghq.com',
        service: 'marketing-site',
        env: import.meta.env.MODE,
        version: props.initialPage.props.version,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0,
        trackResources: false,
        trackUserInteractions: false,
        trackLongTasks: false,
        defaultPrivacyLevel: 'mask-user-input',
        plugins: [reactPlugin({ router: false })],
        beforeSend: (event) => {
          if (event.type === 'error') {
            // Only send errors from the ErrorBoundary component or Ask AI chat to Datadog
            const errorSource = event.context?.source
            return errorSource === 'react-error-boundary' || errorSource === 'ask-ai-chat'
          }
          return true
        },
      })
    }

    if (!import.meta.env.DEV) {
      router.on('navigate', (event) => {
        if (typeof window === 'undefined') return

        const { url } = event.detail.page
        window._hsq.push(['setPath', url])
        window._hsq.push(['trackPageView'])

        if (window.HubSpotConversations) {
          window.HubSpotConversations.widget.refresh()
        }
      })
    }

    // this code must be same as block in ssr.tsx
    const app = (
      <ErrorBoundary>
        <App {...props} />
      </ErrorBoundary>
    )
    // similarities end here

    if (import.meta.env.DEV) {
      createRoot(el).render(app)
    } else {
      hydrateRoot(el, app)
    }
  },
})
